import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, CircularProgress, Stack, useTheme } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle as CheckCircleIcon, Error as ErrorIcon } from '@mui/icons-material';
import apiService from '../../services/apiService';
import AuthBackground from '../../components/Auth/AuthBackground';

const VerifyEmailPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const theme = useTheme();
    const { t } = useTranslation();

    const token = searchParams.get('token');

    const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        if (!token) {
            setStatus('error');
            setErrorMessage(t('verifyEmail.missingToken') || 'Verification token is missing from the URL.');
            return;
        }

        const verifyToken = async () => {
            try {
                const response = await apiService.get(`/auth/verify-email/${token}`);
                if (response.success) {
                    setStatus('success');
                } else {
                    setStatus('error');
                    setErrorMessage(response.message || t('verifyEmail.invalidToken') || 'The verification link is invalid or has expired.');
                }
            } catch (error: any) {
                setStatus('error');
                setErrorMessage(error.response?.data?.message || t('verifyEmail.invalidToken') || 'The verification link is invalid or has expired.');
            }
        };

        verifyToken();
    }, [token, t]);

    return (
        <AuthBackground marqueeText="VERIFIED // YOU'RE IN // LET'S RIDE // JUST VIBES //">
            <Box sx={{ width: '100%', maxWidth: 480, textAlign: 'center' }}>

                {/* Logo/Icon Area */}
                <Box sx={{
                    width: 80,
                    height: 80,
                    borderRadius: 4,
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 32px',
                }}>
                    {status === 'verifying' && <CircularProgress size={40} />}
                    {status === 'success' && <CheckCircleIcon color="success" sx={{ fontSize: 48 }} />}
                    {status === 'error' && <ErrorIcon color="error" sx={{ fontSize: 48 }} />}
                </Box>

                <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, letterSpacing: '-0.02em' }}>
                    {status === 'verifying' && (t('verifyEmail.verifyingTitle') || 'Verifying Email')}
                    {status === 'success' && (t('verifyEmail.successTitle') || 'Email Verified!')}
                    {status === 'error' && (t('verifyEmail.errorTitle') || 'Verification Failed')}
                </Typography>

                <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem', mb: 6 }}>
                    {status === 'verifying' && (t('verifyEmail.verifyingDesc') || 'Please wait while we verify your email address...')}
                    {status === 'success' && (t('verifyEmail.successDesc') || 'Your email address has been successfully verified. You can now log in to your account.')}
                    {status === 'error' && errorMessage}
                </Typography>

                {status === 'success' && (
                    <Button
                        fullWidth
                        variant="contained"
                        size="large"
                        onClick={() => navigate('/login')}
                        sx={{
                            bgcolor: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
                            color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF',
                            py: 1.5,
                            fontWeight: 600,
                            fontSize: '1rem',
                            '&:hover': { bgcolor: theme.palette.mode === 'dark' ? '#f5f5f5' : '#333333' }
                        }}
                    >
                        {t('verifyEmail.goToLogin') || 'Go to Login'}
                    </Button>
                )}

                {status === 'error' && (
                    <Stack spacing={2}>
                        <Button
                            fullWidth
                            variant="contained"
                            size="large"
                            onClick={() => navigate('/login')}
                            sx={{
                                bgcolor: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
                                color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF',
                                py: 1.5,
                                fontWeight: 600,
                                fontSize: '1rem',
                                '&:hover': { bgcolor: theme.palette.mode === 'dark' ? '#f5f5f5' : '#333333' }
                            }}
                        >
                            {t('verifyEmail.returnToLogin') || 'Return to Login'}
                        </Button>
                    </Stack>
                )}
            </Box>
        </AuthBackground>
    );
};

export default VerifyEmailPage;
